import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Archive, Check, FileText, Hand, History, Pencil, X } from 'lucide-react'
import { api, messageErreur } from '../../lib/api'
import type { Donnee, Version } from '../../lib/types'
import { Chargement, Message, Modale, Pastille } from '../base'
import VisionneuseDocument from '../VisionneuseDocument'
import FormulaireDonnee from './FormulaireDonnee'
import * as f from '../../lib/format'
import { detailLisible, resume } from '../../lib/resume'
import { CATEGORIES_DONNEE } from '@doc/shared'

export default function LigneDonnee({
  donnee,
  patientId,
  propose = false,
  sansType = false,
}: {
  donnee: Donnee
  patientId: string
  propose?: boolean
  /** La nature de l'antécédent est déjà le titre du groupe. */
  sansType?: boolean
}) {
  const client = useQueryClient()
  const [fiche, setFiche] = useState(false)
  const [edition, setEdition] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)

  const rafraichir = () => {
    client.invalidateQueries({ queryKey: ['dossier', patientId] })
    client.invalidateQueries({ queryKey: ['accueil'] })
  }

  const action = useMutation({
    mutationFn: (quoi: 'valider' | 'rejeter' | 'archiver') =>
      api(`/api/donnees/${donnee.id}/${quoi}`, { methode: 'POST', corps: {} }),
    onSuccess: rafraichir,
    onError: (e) => setErreur(messageErreur(e)),
  })

  const texte = resume(donnee, { sansType })

  return (
    <li className="group px-5 py-2.5">
      <div className="flex items-start gap-3">
        {/* L'intitulé est le point d'entrée : un clic ouvre le détail, l'origine
            et la source. La ligne reste nue. */}
        <button
          className="min-w-0 flex-1 text-left"
          onClick={() => setFiche(true)}
          title="Voir le détail et la source"
        >
          <span className="flex flex-wrap items-center gap-2">
            <span className="text-[14px] font-medium text-encre group-hover:text-bleu-700">
              {donnee.libelle}
            </span>
            {propose && <Pastille ton="attention">À vérifier</Pastille>}
          </span>
          {texte && (
            <span className="mt-0.5 block text-[13px] leading-relaxed text-ardoise">{texte}</span>
          )}
        </button>

        {/* Pour une proposition, les trois gestes de la vérification restent à
            portée de main ; pour une donnée du dossier, tout est dans la fiche. */}
        {propose && (
          <div className="flex shrink-0 items-center gap-1">
            <button
              className="bouton-doux px-2.5 py-1.5"
              onClick={() => action.mutate('valider')}
              disabled={action.isPending}
              title="Valider et faire entrer au dossier"
            >
              <Check className="h-4 w-4" />
              Valider
            </button>
            <button
              className="bouton-discret px-2"
              onClick={() => setEdition(true)}
              title="Corriger avant de valider"
            >
              <Pencil className="h-4 w-4" />
            </button>
            <button
              className="bouton-discret px-2 text-critique"
              onClick={() => action.mutate('rejeter')}
              title="Rejeter la proposition"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      {erreur && (
        <div className="mt-2">
          <Message ton="critique">{erreur}</Message>
        </div>
      )}

      {fiche && (
        <FicheDonnee
          donnee={donnee}
          fermer={() => setFiche(false)}
          corriger={() => {
            setFiche(false)
            setEdition(true)
          }}
          archiver={() => {
            if (
              window.confirm(
                'Archiver cette information ? Elle quitte les cartes du dossier mais reste consultable dans l’historique.',
              )
            ) {
              setFiche(false)
              action.mutate('archiver')
            }
          }}
        />
      )}

      {edition && (
        <FormulaireDonnee
          patientId={patientId}
          categorie={donnee.categorie}
          donnee={donnee}
          fermer={() => setEdition(false)}
        />
      )}
    </li>
  )
}

/**
 * Tout ce qu'il faut savoir sur une information : son détail, d'où elle vient,
 * qui l'a validée, son histoire. Ce qui encombrait chaque ligne du dossier est
 * rangé ici.
 */
function FicheDonnee({
  donnee,
  fermer,
  corriger,
  archiver,
}: {
  donnee: Donnee
  fermer: () => void
  corriger: () => void
  archiver: () => void
}) {
  const [source, setSource] = useState<number | null>(null)
  const [historique, setHistorique] = useState(false)

  return (
    <Modale
      titre={donnee.libelle}
      sousTitre={`${CATEGORIES_DONNEE[donnee.categorie]}${
        donnee.version > 1 ? ` · version ${donnee.version}` : ''
      }`}
      fermer={fermer}
      piedDePage={
        <>
          <button className="bouton-discret mr-auto" onClick={() => setHistorique(true)}>
            <History className="h-4 w-4" />
            Historique
          </button>
          <button className="bouton-discret text-critique" onClick={archiver}>
            <Archive className="h-4 w-4" />
            Archiver
          </button>
          <button className="bouton-primaire" onClick={corriger}>
            <Pencil className="h-4 w-4" />
            Corriger
          </button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-[13px]">
          {detailLisible(donnee.detail).map((ligne) => (
            <div key={ligne.etiquette}>
              <dt className="text-brume">{ligne.etiquette}</dt>
              <dd className="text-encre">{ligne.valeur}</dd>
            </div>
          ))}
        </dl>

        <div className="border-t border-trait pt-3">
          <h3 className="mb-2 text-[11.5px] font-medium tracking-wide text-brume uppercase">
            D’où vient cette information
          </h3>

          {donnee.sources.length > 0 ? (
            <ul className="flex flex-col gap-2">
              {donnee.sources.map((s, index) => (
                <li key={s.id}>
                  <button
                    className="flex w-full items-start gap-2.5 rounded-lg bg-slate-50 px-3.5 py-2.5 text-left hover:bg-bleu-50"
                    onClick={() => setSource(index)}
                  >
                    <FileText className="mt-0.5 h-4 w-4 shrink-0 text-bleu-500" />
                    <span className="min-w-0">
                      <span className="block text-[13.5px] font-medium">{s.titre}</span>
                      {s.passage && (
                        <span className="mt-0.5 block text-[12.5px] text-brume italic">
                          « {s.passage} »{s.page ? ` — page ${s.page}` : ''}
                        </span>
                      )}
                      <span className="mt-0.5 block text-[12px] text-bleu-600">
                        Ouvrir le document
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="flex items-center gap-2 text-[13px] text-ardoise">
              <Hand className="h-4 w-4 text-brume" />
              Saisie manuelle
              {donnee.creePar ? ` par ${donnee.creePar.nom}` : ''} le {f.dateCourte(donnee.creeLe)}
            </p>
          )}

          {donnee.validePar && (
            <p className="mt-2 text-[12.5px] text-brume">
              Validée par {donnee.validePar.nom} le {f.dateCourte(donnee.valideLe)}
            </p>
          )}
        </div>
      </div>

      {source !== null && donnee.sources[source] && (
        <VisionneuseDocument
          documentId={donnee.sources[source]!.documentId}
          page={donnee.sources[source]!.page}
          passage={donnee.sources[source]!.passage}
          fermer={() => setSource(null)}
        />
      )}

      {historique && <Historique donnee={donnee} fermer={() => setHistorique(false)} />}
    </Modale>
  )
}

const ACTIONS_VERSION: Record<string, string> = {
  creation: 'Création',
  correction: 'Correction',
  validation: 'Validation',
  rejet: 'Rejet',
  archivage: 'Archivage',
  'conflit.rejet': 'Rejetée après un désaccord',
  'conflit.remplacee': 'Remplacée après un désaccord',
  'conflit.remplacement': 'Entrée au dossier après un désaccord',
  'conflit.evolution': 'Évolution datée constatée',
  'conflit.conserve': 'Conservée malgré un désaccord',
  fusion: 'Fusion de dossiers',
}

function Historique({ donnee, fermer }: { donnee: Donnee; fermer: () => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ['versions', donnee.id],
    queryFn: () => api<{ versions: Version[] }>(`/api/donnees/${donnee.id}/versions`),
  })

  return (
    <Modale
      titre={`Historique — ${donnee.libelle}`}
      sousTitre="De la plus récente à la plus ancienne. Rien n'est effacé."
      large
      fermer={fermer}
    >
      {isLoading || !data ? (
        <Chargement />
      ) : (
        <ol className="flex flex-col gap-3">
          {data.versions.map((version) => (
            <li key={version.id} className="rounded-lg bg-slate-50 px-4 py-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[13px] font-medium">
                  Version {version.numero} — {ACTIONS_VERSION[version.action] ?? version.action}
                </span>
                <span className="text-[12px] text-brume">
                  {f.heure(version.creeLe)}
                  {version.auteur ? ` · ${version.auteur}` : ''}
                </span>
              </div>
              {version.raison && (
                <p className="mt-1 text-[12.5px] text-ardoise">Raison : {version.raison}</p>
              )}
              <dl className="mt-2 grid grid-cols-2 gap-x-5 gap-y-1 text-[12.5px]">
                {detailLisible(version.contenu.detail).map((ligne) => (
                  <div key={ligne.etiquette}>
                    <dt className="text-brume">{ligne.etiquette}</dt>
                    <dd className="text-encre">{ligne.valeur}</dd>
                  </div>
                ))}
              </dl>
            </li>
          ))}
        </ol>
      )}
    </Modale>
  )
}
